/**
 * "What was sent" (TMMAPP-0044 / TMMAPI-0071) — the exact post-redaction request body tmm-api
 * stored at the LLM seam. Shown unedited: pretty-printed only when it parses as a JSON object or
 * array, verbatim otherwise; Copy writes the raw payload, never the pretty-printed form. The
 * truncation notice keys on tmm-api's `outboundTruncated` and sizes from `outboundBytes`
 * (TMMAPP-0047) — never on the payload's text.
 */
import { useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { CopyIcon } from '@/components/icons';
import { useTranslation } from 'react-i18next';
import { formatByteSize } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';

/** 2-space JSON when the payload is a JSON object/array; otherwise exactly as received. */
function displayPayload(payload: string): string {
  try {
    const parsed: unknown = JSON.parse(payload);
    return typeof parsed === 'object' && parsed !== null
      ? JSON.stringify(parsed, null, 2)
      : payload;
  } catch {
    return payload;
  }
}

export interface AiSentPayloadProps {
  payload: string;
  /** tmm-api's `outboundTruncated`: only the head of the body is stored. */
  truncated: boolean;
  /** tmm-api's `outboundBytes`: the original body's UTF-8 size. */
  bytes: number | null;
}

export function AiSentPayload({ payload, truncated, bytes }: AiSentPayloadProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [copied, setCopied] = useState(false);

  const copy = () => {
    // Absent outside a secure context — the Copy button then does nothing rather than throw.
    if (typeof navigator.clipboard === 'undefined') return;
    void navigator.clipboard
      .writeText(payload)
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  };

  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}
      >
        <Typography variant="subtitle2" component="h2">
          {t('aiHistory.sentTitle')}
        </Typography>
        <Button size="small" startIcon={<CopyIcon />} onClick={copy}>
          {copied ? t('aiHistory.copied') : t('aiHistory.copy')}
        </Button>
      </Stack>
      {truncated && (
        <Alert severity="info">
          {bytes !== null
            ? t('aiHistory.truncated', { size: formatByteSize(bytes, locale) })
            : t('aiHistory.truncatedUnknownSize')}
        </Alert>
      )}
      <Box
        role="region"
        aria-label={t('aiHistory.sentTitle')}
        tabIndex={0}
        sx={{
          p: 1.5,
          bgcolor: 'action.hover',
          borderRadius: 1,
          overflow: 'auto',
          maxHeight: { xs: 320, md: 480 },
        }}
      >
        <Box component="pre" sx={{ m: 0, fontFamily: 'inherit', whiteSpace: 'pre' }}>
          <Typography variant="mono" component="span" sx={{ whiteSpace: 'pre' }}>
            {displayPayload(payload)}
          </Typography>
        </Box>
      </Box>
    </Stack>
  );
}
